import { useState } from 'react';
import type { OrderStep } from '../../shared/orderStatus';
import type { StepRole } from '../utils/stepProgress';
import { formatDate, relativeTime } from '../utils/time';
import { TrackingDetails } from './TrackingDetails';

interface StepIndicatorProps {
  step: OrderStep;
  role: StepRole;
  isLast: boolean;
  isCore?: boolean;
  /** Show the core charge amount from this step's details. */
  showCoreCharge?: boolean;
  /** The core is back with us, so its return deadline is shown without urgency. */
  coreReturned?: boolean;
}

const nodeClasses: Record<StepRole, string> = {
  done: 'bg-step-completed border-step-completed text-white',
  current: 'bg-step-completed border-step-completed text-white ring-4 ring-brand-100 scale-110',
  next: 'bg-white border-dashed border-step-active text-step-active',
  upcoming: 'bg-white border-step-pending text-step-pending',
};

// The connector below a node leads to the following step.
const connectorClasses: Record<StepRole, string> = {
  done: 'bg-step-completed',
  current: 'bg-gradient-to-b from-step-completed to-slate-200',
  next: 'bg-slate-200',
  upcoming: 'bg-slate-200',
};

const labelClasses: Record<StepRole, string> = {
  done: 'text-slate-700 font-medium',
  current: 'text-brand-700 font-semibold',
  next: 'text-slate-600',
  upcoming: 'text-slate-400',
};

function NodeIcon({ role }: { role: StepRole }) {
  if (role === 'done') {
    return (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  if (role === 'current') {
    return <div className="w-2.5 h-2.5 rounded-full bg-white" />;
  }
  if (role === 'next') {
    return <div className="w-2 h-2 rounded-full bg-step-active" />;
  }
  return <div className="w-2 h-2 rounded-full bg-step-pending" />;
}

export function StepIndicator({
  step,
  role,
  isLast,
  isCore,
  showCoreCharge = false,
  coreReturned = false,
}: StepIndicatorProps) {
  const hasDetails =
    !!step.details &&
    !!(
      step.details.trackingNo ||
      step.details.expirationDate ||
      step.details.assemblyStage ||
      step.details.inspectionResult ||
      (showCoreCharge && step.details.coreBalance !== undefined)
    );
  const [showDetails, setShowDetails] = useState(hasDetails);
  const reached = role === 'done' || role === 'current';

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="relative shrink-0">
          {role === 'current' && (
            <span className="absolute inset-0 rounded-full bg-brand-400 animate-pulse-ring" />
          )}
          <div
            className={`
              relative z-10 flex items-center justify-center
              w-7 h-7 rounded-full border-2 transition-all duration-300
              ${nodeClasses[role]}
            `}
          >
            <NodeIcon role={role} />
          </div>
        </div>
        {!isLast && (
          <div className={`w-0.5 flex-1 min-h-8 ${connectorClasses[role]} transition-colors duration-500`} />
        )}
      </div>

      <div className={`pb-6 ${isLast ? 'pb-0' : ''} min-w-0 flex-1`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm leading-7 ${labelClasses[role]}`}>{step.name}</span>

          {role === 'current' && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide bg-brand-100 text-brand-700">
              Current
            </span>
          )}
          {role === 'next' && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200">
              Up next
            </span>
          )}

          {step.completedAt && reached && (
            <span className="text-xs text-slate-400" title={formatDate(step.completedAt)}>
              {relativeTime(step.completedAt)}
            </span>
          )}

          {hasDetails && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className={`
                text-xs px-2 py-0.5 rounded-full border transition-colors cursor-pointer
                ${
                  showDetails
                    ? isCore
                      ? 'bg-amber-50 border-amber-300 text-amber-700'
                      : 'bg-brand-50 border-brand-300 text-brand-700'
                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300'
                }
              `}
            >
              {showDetails ? 'hide' : 'details'}
            </button>
          )}
        </div>

        {showDetails && step.details && (
          <div className="mt-1.5">
            <TrackingDetails
              details={step.details}
              showCoreCharge={showCoreCharge}
              coreReturned={coreReturned}
            />
          </div>
        )}
      </div>
    </div>
  );
}
